import { TemplatePrompts } from '@kimuno/api-sdk';
import { palette, spaces } from '@kimuno/theme';
import { Stack, Typography } from '@mui/material';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getGeneratedImagesRemainingLimit as getGeneratedImagesRemainingLimitAction } from '../../../../actions/aiAssistant.actions';
import { getAiGeneratedImagesRemainingLimit } from '../../../../reducers/aiAssistant.selectors';
import { selectUser } from '../../../../reducers/user.selectors';
import { aiAssistantContext } from '../../../../state/AIAssistantContextProvider';
import useAction from '../../../../utils/useAction';
import { ActiveView } from '../../shared/constants';
import AIAssistantFooter from '../../shared/Footer/AIAssistantFooter';
import AIAssistantDialogContentWithHeadline from '../../shared/Layout/AIAssistantContentWithHeadline';
import AIAssistantTemplatePromptList from '../../shared/TemplatePromptForm/AIAssistantTemplatePromptList';
import { getTemplatePromptsByView, isImageGenerationTemplate } from '../../shared/utils';
import { AIAssistantPageSelector } from './AIAssistantPageSelector';

const AIAssistantUnitEntry = () => {
	const { t } = useTranslation();
	const {
		processNewConversation,
		isExtended,
		setImageGenerationLimitReachedAlertVisible,
		isPromptLimitReached,
		showPromptLimitAlert,
	} = useContext(aiAssistantContext);

	const user = useSelector(selectUser);
	const remainingLimit = useSelector(getAiGeneratedImagesRemainingLimit);
	const hasRemainingLimit = !!remainingLimit && remainingLimit > 0;
	const fetchRemainingLimit = useAction(getGeneratedImagesRemainingLimitAction);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		fetchRemainingLimit();
	}, []);
	const isLernposterEnabled = !user?.isSchoolUser;

	const allPromptItems = getTemplatePromptsByView(ActiveView.UNIT_ENTRY);
	const promptItems = isLernposterEnabled
		? allPromptItems
		: allPromptItems.filter((item) => item.id !== TemplatePrompts.CREATE_LEARNING_POSTER);

	const contentStyles = {
		position: 'relative',
		padding: 0,
	};

	const handleClick = (title: string, subheader: string, id: TemplatePrompts) => {
		if (isPromptLimitReached) {
			showPromptLimitAlert();
			return;
		}

		if (isImageGenerationTemplate(id) && !hasRemainingLimit) {
			setImageGenerationLimitReachedAlertVisible(true);
			return;
		}

		processNewConversation({
			message: `${title}: ${subheader}`,
			messageContext: { templatePromptId: id },
		});
	};

	return (
		<>
			<AIAssistantDialogContentWithHeadline
				headline={
					<Stack spacing={spaces.xs}>
						<Typography variant="subtitle2" sx={{ color: palette.text.secondary }}>
							{t('AI suggestions for this unit')}
						</Typography>
					</Stack>
				}
				sx={contentStyles}
				data-testid="ai-assistant-unit-entry"
			>
				<Stack sx={{ paddingX: spaces.md, paddingTop: spaces.md }}>
					<AIAssistantPageSelector />
					<AIAssistantTemplatePromptList
						items={promptItems}
						isExtended={isExtended}
						onPromptClick={handleClick}
					/>
				</Stack>
			</AIAssistantDialogContentWithHeadline>
			<AIAssistantFooter
				caption={
					<Typography variant="caption" sx={{ color: palette.text.secondary }}>
						{t('All suggestions are based on the page selection of your currently open document')}
					</Typography>
				}
			/>
		</>
	);
};

export default AIAssistantUnitEntry;
